import { useEffect, useRef, useState } from 'react';

import { logEvent } from '../../lib/analytics';
import { useFeatureFlag } from '../../lib/useFeatureFlags';
import { useVoiceDemo } from '../../providers/VoiceDemoContext';
import { BoringAvatar } from '../BoringAvatar';
import { ChevronDown } from '../icons/ChevronDown';

import { BlurredVoicesUnlock } from './BlurredVoicesUnlock';
import { StickyFooterVoiceCard } from './StickyFooterVoiceCard';
import { VoiceCard } from './VoiceCard';

export function VoiceList() {
  const { currentLanguage, handleUpdateVoice, currentVoice, error } =
    useVoiceDemo();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { variant: moreVoicesVariant, isLoading } = useFeatureFlag(
    'moreVoicesButtonVariant',
    'control',
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isLoading || !isDropdownOpen) return;
    logEvent('ab_test_variant_assigned', {
      testName: 'more_voices_button',
      variant: moreVoicesVariant,
    });
  }, [moreVoicesVariant, isDropdownOpen, isLoading]);

  const toggleDropdown = () => {
    const nextState = !isDropdownOpen;
    if (nextState) {
      logEvent('voice_list_opened', {
        language: currentLanguage.languageCode,
        current_voice: currentVoice.name,
      });
    }
    setIsDropdownOpen(nextState);
  };
  return (
    <div className="relative flex w-full flex-1" ref={dropdownRef}>
      <button
        key={currentVoice.name}
        className={`flex flex-1 cursor-pointer items-center justify-between gap-1 rounded-2xl border px-[16px] py-[8px] font-medium leading-normal hover:bg-[#F5F5FA] @sm:gap-2 ${isDropdownOpen ? 'border-[#9899A6]' : 'border-[#E9EAF0]'}`}
        onClick={toggleDropdown}>
        <div className="flex items-center gap-2">
          {currentVoice.image.length ? (
            <img
              src={currentVoice.image}
              className="min-h-8 min-w-8 rounded-full bg-[#f0f4f9]"
              alt={currentVoice.displayName}
              width={32}
              height={32}
            />
          ) : (
            <BoringAvatar />
          )}
          <div>
            {currentVoice.displayName}
            {!currentVoice.image.length && (
              <>
                &nbsp;
                <span className="hidden @xsm:inline">
                  {'(' + currentVoice.gender.charAt(0).toUpperCase() + ')'}
                </span>
              </>
            )}
          </div>
        </div>
        <ChevronDown />
      </button>
      {isDropdownOpen && (
        <div className="dropdown-shadow absolute top-[56px] z-50 flex max-h-[400px] w-auto flex-col overflow-hidden rounded-2xl border border-[#E9EAF0] bg-white">
          <div className="flex flex-col gap-1 overflow-y-auto p-2 no-scrollbar">
            {currentLanguage.voices.map((voice) => {
              return (
                <VoiceCard
                  gender={voice.gender}
                  key={voice.name}
                  image={voice.image}
                  languageCode={voice.languageCode}
                  isPlaceholder={!!error}
                  title={voice.displayName}
                  onClick={() => {
                    setIsDropdownOpen(false);
                    !error && handleUpdateVoice(voice.name);
                  }}
                  isSelected={voice.name === currentVoice.name}
                  isSizeLarge={true}
                />
              );
            })}
            {moreVoicesVariant === 'A' && (
              <BlurredVoicesUnlock
                voiceId={currentVoice.id}
                languageCode={currentLanguage.languageCode}
                testVariant={moreVoicesVariant}
                voicePreviews={currentLanguage.voices.slice(0, 5).map((v) => ({
                  displayName: v.displayName,
                  image: v.image,
                }))}
              />
            )}
          </div>

          {moreVoicesVariant === 'B' && (
            <div className="border-t border-[#E9EAF0] bg-white p-2 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
              <StickyFooterVoiceCard
                voiceId={currentVoice.id}
                languageCode={currentLanguage.languageCode}
                testVariant={moreVoicesVariant}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
